import _ from 'lodash';
import autobind from 'autobind-decorator';

import {
	action,
	observe,
	computed,
	observable,
	runInAction,
	extendObservable
} from 'mobx';

import { getAx } from 'shared/util';

const commonOptions = {
	contentType: 'application/json',
	accept: 'application/json'
};

const apiPath = '/api';

const DFLT_PAGE_LIMIT = 12;

class BaseStore {
	changeTypes = {
		UPDATE: 'update'
	}

	// Axios instance
	ax = getAx();

	/**
	 * data - Keep an object for adding new data from forms
	 * @type {Array}
	 */
	@observable
	_data = {
		item: observable.map({})
	};

	/**
	 * items - Keep an array of this stores data items
	 * @type {Array}
	 */
	@observable
	_items = [];

	@computed
	get items () {
		return this._items;
	}

	set items (items) {
		this._items = items;
	}

	/**
	 * pagination - Stores pagination data for lists of items
	 * @type {Object}
	 */
	@observable
	_pagination = {};

	@computed
	get pagination () {
		return this._pagination;
	}

	set pagination (pagination) {
		this._pagination = pagination;
	}

	/**
	 * isLoading - If genreal I/O async actions are currently taking place
	 * @type {Boolean}
	 */
	@observable
	_isLoading = false;

	@computed
	get isLoading () {
		return this._isLoading;
	}

	set isLoading (loading) {
		this._isLoading = loading;
	}

	@action
	setIsLoading (isLoading) {
		this._isLoading = isLoading;
	}

	/**
	 * arePagesLoading - If page related I/O async actions are currently taking place
	 * @type {Boolean}
	 */
	@observable
	_arePagesLoading = false;

	@computed
	get arePagesLoading () {
		return this._arePagesLoading;
	}

	set arePagesLoading (loading) {
		this._arePagesLoading = loading;
	}

	onDataChange () {}

	@action
	clearDataItem () {
		this._data.item.clear();
	}

	/**
	 * Fetch a list of items from the API and
	 * update the observable _items property.
	 */
 	@autobind
	@action
	async fetchItems (_id = '') {
		const apiRoute = _.result(this, 'apiRoute', null);

		if (_.isNull(apiRoute)) {

			console.warn('[BaseStore.fetchItems] No apiRoute set');
			return;
		}

		this._isLoading = true;

		try {

			const { data } = await this.ax.get(`${apiRoute}/${_id}`);
			runInAction('Update with fetched user list', () => {
				this._items = data;
			});
		} catch (err) {

			console.error('[ERROR: BaseStore.fetchItems]', err);
		} finally {

			this._isLoading = false;
		}
	}

	/**
	 * Fetch a list of paginated items from the API
	 * and update the observable _pagination property.
	 */
	@action
	async fetchPagedItems (page = 1, limit = DFLT_PAGE_LIMIT) {
		const fetchPagedItemsRoute = _.result(this, 'fetchPagedItemsRoute', null);

		if (_.isNull(fetchPagedItemsRoute)) {

			console.warn('[BaseStore.fetchItemsPaged] No fetchPagedItemsRoute set');
			return;
		}

		this.arePagesLoading = true;

		try {

			const route = fetchPagedItemsRoute
				.replace(/\:page/, page)
				.replace(/\:limit/, limit);
			const { data } = await this.ax.get(route);
			runInAction('Update with fetched paginated user list', () => {
				this._pagination = observable(data);
			});
		} catch (err) {

			console.error('[ERROR: BaseStore.fetchPagedItems]', err);
		} finally {

			this.arePagesLoading = false;
		}
	}

	@action
	async saveItem (payload, opts = {}) {
		const apiRoute = opts.route;

		if (_.isNull(apiRoute)) {

			console.warn('[BaseStore.fetchItems] No apiRoute set');
			return;
		}

		const { silent = false } = opts;

		this.isLoading = silent !== true;

		try {

			const { data } = await this.saveData(payload, apiRoute);
			runInAction('Update with saved user', () => {
				this._items.unshift(data);
			});

			return data;
		} catch (err) {

			console.error('[ERROR: BaseStore.saveItem]', err);
		} finally {

			this.isLoading = false;
		}
	}

	@action
	async updateItem (change, opts = {}) {
		const apiRoute = _.result(this, 'apiRoute', null);

		if (_.isNull(apiRoute)) {

			console.warn('[BaseStore.fetchItems] No apiRoute set');
			return;
		}

		const { _id = null } = change;
		
		if (_.isNull(_id)) {
			
			return;
		}
		
		
		const route = `${apiRoute}/${_id}`;
		console.log(`[ARTIST UPDATE] ${route}`);
		const payload = {
			...change,
			type: this.changeTypes.UPDATE
		};

		if (opts.silent !== true) {

			// Must wrap this in an if statement to not trigger changes
			this.isLoading = true;
		}

		try {

			const { data } = await this.saveData(payload, route);

			runInAction('Update with updated user', () => {
				this._items = this._items.slice().map((item) => (
					item._id === _id ? data : item
				));
			});

			return data;
		} catch (err) {

			console.error('[ERROR: BaseStore.updateItem]', err);
		} finally {

			if (opts.silent !== true) {

				// Must wrap this in an if statement to not trigger changes
				this.isLoading = false;
			}
		}
	}

	@action
	async deleteItem (item) {
		const apiRoute = _.result(this, 'apiRoute', null);

		if (_.isNull(apiRoute)) {

			console.warn('[BaseStore.fetchItems] No apiRoute set');
			return;
		}

		try {

			await this.ax.delete(`${apiRoute}/${item._id}`);
			this._items = this._items
				.filter(({ _id }) => _id !== item._id);
		} catch (err) {

			console.error('[ERROR: BaseStore.deleteItem]', err);
		}
	}

	get (key, _id = null) {
		const getKey = key.replace(/^\w+\./, '');

		if (_id === null) {

			return this._data.item.get(getKey);
		}

		const index = this.getItemIndex({ _id });
		return this.items[index][getKey];
	}

	@action
	set ({ key, value, _id = null, silent = false }) {
		const setKey = key.replace(/^\w+\./, '');
		this.silentDataUpdate = silent;

		if (_.isNull(_id)) {

			this._data.item.set(setKey, value);
			return;
		}

		const index = this.getItemIndex({ _id });
		this.items[index][setKey] = value;
	}

	@action
	async saveData (change, route) {
		if (this.silentDataUpdate) {

			this.silentDataUpdate = false;
			return;
		}

		const payload = _.isObject(change.object) ?
			_.pick(change.object, change.name) : change;

		try {

			switch (change.type) {
				case this.changeTypes.UPDATE:
					return await this.ax.put(route, payload);

				default:
					return await this.ax.post(route, payload);
			}
		} catch (err) {

			console.error('[ERROR: BaseStore.saveData]', err);
		}
	}

	updateCollectionItem ({ key, value, _id, save = false }) {
		const index = this.getItemIndex({ _id });

		if (this._items[index][key] === value) {

			return;
		}

		this._items[index][key] = value;

		if (save) {

			this.updateItem(this._items[index]);
		}
	}

	getItemIndex (item) {
		if (_.isEmpty(this._items)) {

			return -1;
		}

		return this._items.findIndex(({ _id }) => _id === item._id);
	}

	getItemByID (item) {
		return this._items.find(({ _id }) => _id === item._id);
	}
}

export default BaseStore;
